import { forwardRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";
import { login as loginApi } from "@/api/auth";
import { ApiError } from "@/api/client";
import { Layout } from "@/components/Layout";
import { useInvalidateAuth } from "@/hooks/useAuth";

const schema = z.object({
  email: z.string().email("Adresse email invalide"),
  password: z.string().min(1, "Mot de passe requis"),
});

type FormData = z.infer<typeof schema>;

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const invalidateAuth = useInvalidateAuth();
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const mutation = useMutation({
    mutationFn: loginApi,
    onSuccess: async () => {
      await invalidateAuth();
      const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
      navigate(from || "/dashboard", { replace: true });
    },
    onError: (err: ApiError) => {
      if (err.code === "EMAIL_NOT_VERIFIED") {
        setFormError("Votre email n'est pas encore vérifié. Vérifiez votre boîte de réception.");
      } else if (err.code === "INVALID_CREDENTIALS") {
        setFormError("Email ou mot de passe incorrect.");
      } else {
        setFormError("Une erreur est survenue, veuillez réessayer.");
      }
    },
  });

  const onSubmit = (data: FormData) => {
    setFormError(null);
    mutation.mutate(data);
  };

  return (
    <Layout>
      <div className="mx-auto max-w-md px-6 py-12">
        <div className="lg-card p-8">
        <h1 className="text-2xl font-bold">Se connecter</h1>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4" noValidate>
          <Field
            label="Email"
            type="email"
            autoComplete="email"
            error={errors.email?.message}
            {...register("email")}
          />
          <Field
            label="Mot de passe"
            type="password"
            autoComplete="current-password"
            error={errors.password?.message}
            {...register("password")}
          />

          {formError && (
            <p role="alert" className="text-sm text-red-700">
              {formError}
            </p>
          )}

          <button
            type="submit"
            disabled={mutation.isPending}
            className="w-full rounded bg-blue-900 px-4 py-2 text-white hover:bg-blue-800 disabled:opacity-50"
          >
            {mutation.isPending ? "Connexion…" : "Se connecter"}
          </button>
        </form>

        <p className="mt-6 text-sm text-gray-600">
          Pas encore de compte ?{" "}
          <Link to="/register" className="text-blue-900 underline">
            S&apos;inscrire
          </Link>
        </p>
        <p className="mt-2 text-sm text-gray-600">
          Mot de passe oublié ?{" "}
          <Link to="/password-reset" className="text-blue-900 underline">
            Réinitialiser
          </Link>
        </p>
        </div>
      </div>
    </Layout>
  );
}

type FieldProps = React.InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  error?: string;
};

const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, error, id, ...rest },
  ref,
) {
  const inputId = id ?? rest.name;
  return (
    <div>
      <label htmlFor={inputId} className="mb-1 block text-sm font-medium">
        {label}
      </label>
      <input
        ref={ref}
        id={inputId}
        className="w-full rounded border border-gray-300 px-3 py-2 focus:border-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-700"
        aria-invalid={!!error}
        aria-describedby={error ? `${inputId}-err` : undefined}
        {...rest}
      />
      {error && (
        <p id={`${inputId}-err`} role="alert" className="mt-1 text-xs text-red-700">
          {error}
        </p>
      )}
    </div>
  );
});
